import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { Btn } from "../../admin/AdminUI";
import { Camera, Upload, Image as ImageIcon, Star, Trash2, ArrowLeft, ArrowRight, Loader2, X } from "lucide-react";

// Reusable Before/After (or Job) photo manager sitting on top of the Media Library.
// value: array of { id, caption? }. onChange(newArray). First item is the primary image.
export default function PhotoManager({ label, value = [], onChange }) {
  const camRef = useRef(); const upRef = useRef();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showLib, setShowLib] = useState(false);
  const [lib, setLib] = useState([]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true); setProgress(0);
    try {
      const r = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || e.loaded || 1)) * 100)),
      });
      onChange([...(value || []), { id: r.data.id, caption: "" }]);
      toast.success("Upload complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try again.");
    } finally { setBusy(false); setProgress(0); if (camRef.current) camRef.current.value = ""; if (upRef.current) upRef.current.value = ""; }
  };

  const openLib = async () => { setShowLib(true); try { const r = await api.get("/admin/media"); setLib(r.data.filter((m) => !m.is_video)); } catch { setLib([]); } };
  const addFromLib = (id) => { if (!(value || []).some((v) => v.id === id)) onChange([...(value || []), { id, caption: "" }]); setShowLib(false); };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= value.length) return; const a = [...value]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  const makePrimary = (i) => { if (i === 0) return; const a = [...value]; const [it] = a.splice(i, 1); a.unshift(it); onChange(a); };
  const setCaption = (i, cap) => { const a = [...value]; a[i] = { ...a[i], caption: cap }; onChange(a); };

  return (
    <div data-testid={`photo-manager-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-bold text-[var(--navy)] uppercase tracking-wide">{label}</label>
        {busy && <span className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading… {progress}%</span>}
      </div>

      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid={`cam-${label}`} />
      <input ref={upRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid={`up-${label}`} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Btn variant="ghost" onClick={() => camRef.current?.click()} disabled={busy} className="!py-3" data-testid={`take-${label}`}><Camera className="w-4 h-4" /> Take Photo</Btn>
        <Btn variant="ghost" onClick={() => upRef.current?.click()} disabled={busy} className="!py-3" data-testid={`upload-${label}`}><Upload className="w-4 h-4" /> Upload Photo</Btn>
        <Btn variant="ghost" onClick={openLib} disabled={busy} className="!py-3" data-testid={`library-${label}`}><ImageIcon className="w-4 h-4" /> Media Library</Btn>
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-gray-400 mt-2">No photos yet. Tap Take Photo at the job site or upload from your gallery.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          {value.map((im, i) => (
            <div key={`${im.id}-${i}`} className="relative border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid={`thumb-${label}-${i}`}>
              <img src={resolveImage(im.id)} alt={im.caption || ""} className="h-28 w-full object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 bg-[var(--red)] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">PRIMARY</span>}
              <div className="flex items-center justify-between px-1.5 py-1 bg-gray-50">
                <div className="flex gap-1">
                  <button title="Move left" onClick={() => move(i, -1)} className="p-1 hover:text-[var(--action)]"><ArrowLeft className="w-3.5 h-3.5" /></button>
                  <button title="Move right" onClick={() => move(i, 1)} className="p-1 hover:text-[var(--action)]"><ArrowRight className="w-3.5 h-3.5" /></button>
                  {i !== 0 && <button title="Make primary" onClick={() => makePrimary(i)} className="p-1 hover:text-[var(--red)]"><Star className="w-3.5 h-3.5" /></button>}
                </div>
                <button title="Remove" onClick={() => remove(i)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <input value={im.caption || ""} onChange={(e) => setCaption(i, e.target.value)} placeholder="Caption (optional)"
                className="w-full text-[11px] px-2 py-1 border-t border-gray-100 focus:outline-none" />
            </div>
          ))}
        </div>
      )}

      {showLib && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowLib(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="media-picker">
            <div className="flex items-center justify-between px-5 py-4 border-b"><h3 className="font-bold text-[var(--navy)] uppercase">Choose From Media Library</h3><button onClick={() => setShowLib(false)}><X className="w-5 h-5" /></button></div>
            <div className="p-4">
              {lib.length === 0 ? <p className="text-sm text-gray-400 p-6 text-center">No images in the library yet.</p> : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {lib.map((m) => (
                    <button key={m.id} onClick={() => addFromLib(m.id)} className="border border-gray-200 rounded-lg overflow-hidden hover:ring-2 ring-[var(--action)]" data-testid={`lib-pick-${m.id}`}>
                      <img src={resolveImage(m.id)} alt={m.original_filename} className="h-24 w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
